/* eslint-disable react-hooks/exhaustive-deps */
import { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { api } from "./api";

const Ctx = createContext(null);

const MEDIA_TYPES = ["video", "audio", "instrumental", "podcast"];

// Split a multi-artist credit string like "Shao, Elaztic feat. Jynx" into individual names.
const ARTIST_SPLIT_RE = /\s*(?:,|&|\+|\bx\b|\bfeat\.?\b|\bft\.?\b|\bwith\b|\bvs\.?\b)\s*/i;
function splitArtists(raw) {
  if (!raw) return [];
  return raw.split(ARTIST_SPLIT_RE).map((s) => s.trim()).filter(Boolean);
}

function shuffled(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export function MediaPlayerProvider({ children }) {
  const [tracks, setTracks] = useState([]);
  const [playerCfg, setPlayerCfg] = useState(null);
  const [mediaType, setMediaType] = useState("audio");
  const [search, setSearch] = useState("");
  const [artistFilter, setArtistFilter] = useState("");
  const [genreFilter, setGenreFilter] = useState("");
  const [currentId, setCurrentId] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState({ time: 0, duration: 0 });
  const [pitch, setPitch] = useState(0); // semitones -6..+6
  const [shuffle, setShuffle] = useState(false);
  const [repeat, setRepeat] = useState(false);
  const [shuffleOrder, setShuffleOrder] = useState([]);
  const [showMini, setShowMini] = useState(true);
  const [volume, setVolume] = useState(() => {
    if (typeof window === "undefined") return 1;
    const v = parseFloat(window.localStorage.getItem("syndafam-volume"));
    return Number.isFinite(v) ? Math.max(0, Math.min(1, v)) : 1;
  });
  const [muted, setMuted] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem("syndafam-muted") === "1";
  });
  // Set to true by the on-page FamRadioPlayer while it is in the viewport; used to hide the MiniPlayer.
  const [fullPlayerVisible, setFullPlayerVisible] = useState(false);
  // DOM element registered by the on-page FamRadioPlayer where the YouTube
  // video should render visually. When null, the persistent iframe stays
  // off-screen (audio-only). When set AND fullPlayerVisible is true, the
  // iframe is repositioned to overlay this element so the visitor sees the
  // actual YouTube video (not just a static thumbnail).
  const [videoSlot, setVideoSlot] = useState(null);

  // ---- EQ (5-band, Web Audio API) --------------------------------------
  // Only affects HTML5 <audio> playback. YouTube iframe audio is cross-origin
  // and cannot be processed by Web Audio, so EQ silently no-ops for YT tracks.
  // Bands: [bass 60Hz lowshelf, lo-mid 250Hz peaking, mid 1kHz peaking,
  //         hi-mid 4kHz peaking, treble 12kHz highshelf]
  const EQ_BANDS_DEFAULT = [0, 0, 0, 0, 0];
  const EQ_PRESETS = useMemo(() => ({
    flat:        [0, 0, 0, 0, 0],
    "bass-boost":[8, 5, 0, -1, 0],
    vocal:       [-2, -1, 4, 3, 1],
    rock:        [5, 2, -1, 3, 4],
    electronic:  [6, 2, -2, 2, 5],
    acoustic:    [3, 2, 3, 1, 2],
    hip_hop:     [7, 3, -1, 2, 3],
  }), []);
  const [eqEnabled, setEqEnabled] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem("syndafam-eq-enabled") === "1";
  });
  const [eqPreset, setEqPreset] = useState(() => {
    if (typeof window === "undefined") return "flat";
    return window.localStorage.getItem("syndafam-eq-preset") || "flat";
  });
  const [eqBands, setEqBands] = useState(() => {
    if (typeof window === "undefined") return EQ_BANDS_DEFAULT;
    try {
      const raw = window.localStorage.getItem("syndafam-eq-bands");
      const parsed = raw ? JSON.parse(raw) : null;
      if (Array.isArray(parsed) && parsed.length === 5) {
        return parsed.map((n) => Math.max(-12, Math.min(12, Number(n) || 0)));
      }
    } catch { /* bad json */ }
    return EQ_BANDS_DEFAULT;
  });
  const audioCtxRef = useRef(null);
  const eqSourceRef = useRef(null);
  const eqFiltersRef = useRef(null);
  const eqBypassRef = useRef(null);

  const audioRef = useRef(null);
  const ytIframeRef = useRef(null);
  const ytRateArmed = useRef(false);

  // Load tracks once — also refetch when the user tabs back or navigates back to the page,
  // so tracks added via Admin show up without a hard refresh.
  const refresh = useCallback(() => {
    api.get("/playlist").then((r) => setTracks(r.data || [])).catch(() => {});
    api.get("/player/config").then((r) => setPlayerCfg(r.data || null)).catch(() => {});
  }, []);

  useEffect(() => {
    refresh();
    const onFocus = () => refresh();
    const onVisible = () => { if (!document.hidden) refresh(); };
    // Custom in-app broadcast — Admin dispatches this after saving the
    // player config so the FamRadio tabs refresh instantly in the same
    // tab (no wait for focus/visibility events, which don't fire when the
    // admin just navigates between /admin and / without a tab switch).
    const onConfigUpdated = () => refresh();
    // Cross-tab sync via BroadcastChannel so a second tab / window also
    // picks up admin edits without a manual reload. Falls back silently
    // in browsers that don't support the API.
    let bc = null;
    try {
      if (typeof BroadcastChannel !== "undefined") {
        bc = new BroadcastChannel("syndafam:player-config");
        bc.onmessage = () => refresh();
      }
    } catch (_) { /* ignore */ }
    // Safety net polling — remote changes (admin editing on their own
    // machine while a fan already has Home open elsewhere) still land
    // within 60 seconds even if the visibility / broadcast pathways miss.
    const poll = setInterval(refresh, 60000);
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("syndafam:player-config-updated", onConfigUpdated);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("syndafam:player-config-updated", onConfigUpdated);
      if (bc) { try { bc.close(); } catch (_) { /* ignore */ } }
      clearInterval(poll);
    };
  }, [refresh]);

  // Filter tracks by mediaType + artist/genre + search
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const af = artistFilter.trim().toLowerCase();
    return tracks.filter((t) => {
      const types = Array.isArray(t.media_types) && t.media_types.length
        ? t.media_types
        : [t.media_type || "audio"];
      if (!types.includes(mediaType)) return false;
      if (af) {
        const names = splitArtists(t.artist || "").map((n) => n.toLowerCase());
        const rawMatch = (t.artist || "").toLowerCase() === af;
        if (!rawMatch && !names.includes(af)) return false;
      }
      if (genreFilter && (t.genre || "").toLowerCase() !== genreFilter.toLowerCase()) return false;
      if (q) {
        const hay = `${t.title || ""} ${t.artist || ""} ${t.genre || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [tracks, mediaType, artistFilter, genreFilter, search]);

  // Distinct artists/genres across the WHOLE library — each credited artist counted individually
  const facets = useMemo(() => {
    const arts = new Map();
    const gens = new Map();
    for (const t of tracks) {
      const names = splitArtists(t.artist || "");
      if (names.length) {
        for (const n of names) arts.set(n, (arts.get(n) || 0) + 1);
      } else if (t.artist) {
        arts.set(t.artist, (arts.get(t.artist) || 0) + 1);
      }
      if (t.genre) gens.set(t.genre, (gens.get(t.genre) || 0) + 1);
    }
    return {
      artists: [...arts.entries()].map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count),
      genres: [...gens.entries()].map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count),
    };
  }, [tracks]);

  const currentTrack = useMemo(() => tracks.find((t) => t.id === currentId), [tracks, currentId]);

  // Rebuild shuffle order whenever filter set or shuffle toggle changes
  useEffect(() => {
    if (shuffle) setShuffleOrder(shuffled(filtered.map((t) => t.id)));
    else setShuffleOrder([]);
  }, [shuffle, mediaType, artistFilter, genreFilter]);

  // Auto-pick the first track when mediaType changes & no current track (or current isn't in filter)
  useEffect(() => {
    if (!currentTrack || currentTrack.media_type !== mediaType) {
      if (filtered.length > 0) setCurrentId(filtered[0].id);
      else setCurrentId(null);
    }
  }, [mediaType, filtered.length]);

  const playTrack = useCallback((id) => {
    setCurrentId(id);
    setPlaying(true);
  }, []);

  const togglePlay = useCallback(() => setPlaying((p) => !p), []);

  const advance = useCallback((dir = 1) => {
    if (!filtered.length) return;
    const order = shuffle && shuffleOrder.length ? shuffleOrder : filtered.map((t) => t.id);
    const idx = Math.max(0, order.indexOf(currentId));
    const next = (idx + dir + order.length) % order.length;
    setCurrentId(order[next]);
    setPlaying(true);
  }, [filtered, shuffle, shuffleOrder, currentId]);

  const nextTrack = useCallback(() => advance(+1), [advance]);
  const prevTrack = useCallback(() => advance(-1), [advance]);

  const onEnded = useCallback(() => {
    if (repeat && audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => {});
      return;
    }
    nextTrack();
  }, [repeat, nextTrack]);

  // Apply pitch to <audio> element (playbackRate + preservesPitch=false)
  useEffect(() => {
    if (!audioRef.current) return;
    audioRef.current.preservesPitch = false;
    audioRef.current.mozPreservesPitch = false;
    audioRef.current.webkitPreservesPitch = false;
    audioRef.current.playbackRate = Math.pow(2, pitch / 12);
  }, [pitch, currentTrack]);

  // Time / duration updates
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const upd = () => setProgress({ time: a.currentTime || 0, duration: a.duration || 0 });
    a.addEventListener("timeupdate", upd);
    a.addEventListener("loadedmetadata", upd);
    a.addEventListener("durationchange", upd);
    return () => {
      a.removeEventListener("timeupdate", upd);
      a.removeEventListener("loadedmetadata", upd);
      a.removeEventListener("durationchange", upd);
    };
  }, [currentTrack]);

  // Play / pause sync
  useEffect(() => {
    const a = audioRef.current;
    if (!a || !currentTrack || currentTrack.youtube_id) return;
    if (playing) a.play().catch(() => setPlaying(false));
    else a.pause();
  }, [playing, currentTrack]);

  // YouTube control via postMessage
  useEffect(() => {
    if (!currentTrack || !currentTrack.youtube_id) return;
    const iframe = ytIframeRef.current;
    if (!iframe || !iframe.contentWindow) return;
    try {
      iframe.contentWindow.postMessage(
        JSON.stringify({ event: "command", func: playing ? "playVideo" : "pauseVideo", args: [] }),
        "*"
      );
    } catch { /* cross-origin */ }
  }, [playing, currentTrack]);

  // Volume + mute sync — HTML5 audio element (audio-file tracks)
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    a.volume = volume;
    a.muted = muted;
  }, [volume, muted, currentTrack]);

  // Volume + mute sync — YouTube iframe (via postMessage)
  useEffect(() => {
    if (!currentTrack || !currentTrack.youtube_id) return;
    const iframe = ytIframeRef.current;
    if (!iframe || !iframe.contentWindow) return;
    try {
      iframe.contentWindow.postMessage(
        JSON.stringify({ event: "command", func: muted ? "mute" : "unMute", args: [] }),
        "*"
      );
      iframe.contentWindow.postMessage(
        JSON.stringify({ event: "command", func: "setVolume", args: [Math.round(volume * 100)] }),
        "*"
      );
    } catch { /* cross-origin */ }
  }, [volume, muted, currentTrack, playing]);

  // Persist volume + mute across sessions
  useEffect(() => {
    try { window.localStorage.setItem("syndafam-volume", String(volume)); } catch { /* storage */ }
  }, [volume]);
  useEffect(() => {
    try { window.localStorage.setItem("syndafam-muted", muted ? "1" : "0"); } catch { /* storage */ }
  }, [muted]);

  // ---- EQ wiring -------------------------------------------------------
  // Lazily build a Web Audio graph the first time an HTML5 <audio> element
  // becomes available. IMPORTANT: this only works when the audio source is
  // same-origin OR sends proper CORS headers. Cross-origin audio (e.g. raw
  // Dropbox links) routed through `MediaElementSource` will play SILENT —
  // Chrome outputs zeroes and logs "MediaElementAudioSource outputs zeroes
  // due to CORS access restrictions". So we ALWAYS check the source origin
  // first and skip the Web Audio graph entirely if the source is cross-origin
  // — the audio element then plays direct with no EQ but full sound.
  const [eqAvailable, setEqAvailable] = useState(false);
  useEffect(() => {
    const el = audioRef.current;
    if (!el) { setEqAvailable(false); return undefined; }
    // Skip for YT tracks — they don't attach to audioRef.
    if (currentTrack?.youtube_id) { setEqAvailable(false); return undefined; }

    // Same-origin check — anything other than same-origin (or a data:/blob:
    // URL, which are treated as opaque) is treated as cross-origin and skipped.
    let sameOrigin = false;
    try {
      const src = el.src || currentTrack?.audio_url || "";
      if (src.startsWith("blob:") || src.startsWith("data:")) sameOrigin = true;
      else if (src.startsWith("/") || src.startsWith("./")) sameOrigin = true;
      else {
        const u = new URL(src, window.location.href);
        sameOrigin = u.origin === window.location.origin;
      }
    } catch { sameOrigin = false; }

    if (!sameOrigin) {
      // Web Audio wiring skipped — audio plays direct so it stays audible.
      eqFiltersRef.current = null;
      setEqAvailable(false);
      return undefined;
    }

    // Same-origin path — safe to wire the EQ graph.
    const buildGraph = () => {
      try {
        const AC = window.AudioContext || window.webkitAudioContext;
        if (!AC) return; // browser without WebAudio
        if (!audioCtxRef.current) audioCtxRef.current = new AC();
        const ctx = audioCtxRef.current;
        if (!ctx._eqSources) ctx._eqSources = new WeakMap();
        let src = ctx._eqSources.get(el);
        if (!src) {
          src = ctx.createMediaElementSource(el);
          ctx._eqSources.set(el, src);
        }
        eqSourceRef.current = src;

        const freqs = [60, 250, 1000, 4000, 12000];
        const types = ["lowshelf", "peaking", "peaking", "peaking", "highshelf"];
        const filters = freqs.map((f, i) => {
          const filt = ctx.createBiquadFilter();
          filt.type = types[i];
          filt.frequency.value = f;
          filt.Q.value = 1;
          filt.gain.value = 0;
          return filt;
        });
        eqFiltersRef.current = filters;

        try { src.disconnect(); } catch { /* first-time */ }
        let node = src;
        for (const f of filters) {
          node.connect(f);
          node = f;
        }
        node.connect(ctx.destination);
        eqBypassRef.current = false;
        setEqAvailable(true);
      } catch (err) {
        console.warn("[EQ] setup failed", err);
        eqFiltersRef.current = null;
        setEqAvailable(false);
      }
    };
    buildGraph();
    const resume = () => {
      const ctx = audioCtxRef.current;
      if (ctx && ctx.state === "suspended") ctx.resume().catch(() => {});
    };
    window.addEventListener("pointerdown", resume, { passive: true });
    window.addEventListener("keydown", resume);
    return () => {
      window.removeEventListener("pointerdown", resume);
      window.removeEventListener("keydown", resume);
    };
  }, [currentTrack?.id, currentTrack?.youtube_id, currentTrack?.audio_url]);

  // Push current band values into the filter graph whenever they change or
  // the EQ is toggled on/off. When disabled we flatten every band to 0dB so
  // the audio path stays intact (no reconnect flicker) but sounds unaffected.
  useEffect(() => {
    const filters = eqFiltersRef.current;
    if (!filters) return;
    const effective = eqEnabled ? eqBands : EQ_BANDS_DEFAULT;
    for (let i = 0; i < filters.length; i++) {
      const val = Math.max(-12, Math.min(12, Number(effective[i]) || 0));
      try {
        filters[i].gain.setTargetAtTime(val, audioCtxRef.current?.currentTime || 0, 0.05);
      } catch {
        filters[i].gain.value = val;
      }
    }
  }, [eqBands, eqEnabled, currentTrack?.id]);

  // Persist EQ settings.
  useEffect(() => {
    try { window.localStorage.setItem("syndafam-eq-enabled", eqEnabled ? "1" : "0"); } catch { /* storage */ }
  }, [eqEnabled]);
  useEffect(() => {
    try { window.localStorage.setItem("syndafam-eq-preset", eqPreset); } catch { /* storage */ }
  }, [eqPreset]);
  useEffect(() => {
    try { window.localStorage.setItem("syndafam-eq-bands", JSON.stringify(eqBands)); } catch { /* storage */ }
  }, [eqBands]);

  const applyEqPreset = useCallback((key) => {
    const preset = EQ_PRESETS[key];
    if (!preset) return;
    setEqPreset(key);
    setEqBands([...preset]);
    setEqEnabled(true);
  }, [EQ_PRESETS]);

  const resetEq = useCallback(() => {
    setEqBands([...EQ_BANDS_DEFAULT]);
    setEqPreset("flat");
  }, []);

  const updateEqBand = useCallback((index, valueDb) => {
    setEqBands((prev) => {
      const next = [...prev];
      next[index] = Math.max(-12, Math.min(12, Number(valueDb) || 0));
      return next;
    });
    setEqPreset("custom");
  }, []);

  // Listen for YT ENDED
  useEffect(() => {
    const onMsg = (e) => {
      if (typeof e.data !== "string") return;
      try {
        const m = JSON.parse(e.data);
        if (m.event === "onStateChange" && m.info === 0) onEnded();
      } catch { /* not YT */ }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [onEnded]);

  const seek = useCallback((t) => {
    if (audioRef.current && !currentTrack?.youtube_id) {
      audioRef.current.currentTime = t;
      setProgress((p) => ({ ...p, time: t }));
    }
  }, [currentTrack]);

  // Auto-play the first audio track on first arrival per session.
  // Modern browsers block unmuted autoplay, so we (a) try immediately, and
  // (b) arm a one-shot "first user gesture" fallback that starts playback on
  // the visitor's first click/keypress/scroll if the browser blocked it.
  const autoplayAttempted = useRef(false);
  const location = useLocation();

  // Deep-link support: `?track=<id>` opens the site with a specific song
  // cued up and playing. This runs on mount and whenever the query string
  // changes (SPA navigation). It short-circuits the first-track autoplay
  // logic below when a specific track is requested.
  const deeplinkedTrackId = useRef(null);
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const trackId = params.get("track");
    if (!trackId || tracks.length === 0) return;
    if (deeplinkedTrackId.current === trackId) return;
    const match = tracks.find((t) => t.id === trackId);
    if (!match) return;

    deeplinkedTrackId.current = trackId;
    autoplayAttempted.current = true;

    // Switch to a media-type this track actually belongs to so it appears in
    // the filtered library.
    const types = Array.isArray(match.media_types) && match.media_types.length
      ? match.media_types
      : [match.media_type || "audio"];
    if (!types.includes(mediaType)) setMediaType(types[0] || "audio");
    setCurrentId(match.id);
    setMuted(false);
    setPlaying(true);

    // Same "one-shot gesture" fallback if the browser blocked play()
    const kick = () => {
      setPlaying(true);
      window.removeEventListener("pointerdown", kick);
      window.removeEventListener("keydown", kick);
      window.removeEventListener("touchstart", kick);
      window.removeEventListener("scroll", kick);
    };
    window.addEventListener("pointerdown", kick, { once: true, passive: true });
    window.addEventListener("keydown", kick, { once: true });
    window.addEventListener("touchstart", kick, { once: true, passive: true });
    window.addEventListener("scroll", kick, { once: true, passive: true });
    return () => {
      window.removeEventListener("pointerdown", kick);
      window.removeEventListener("keydown", kick);
      window.removeEventListener("touchstart", kick);
      window.removeEventListener("scroll", kick);
    };
  }, [location.search, tracks.length]);

  useEffect(() => {
    if (autoplayAttempted.current) return;
    if (tracks.length === 0) return;

    // If a track deep link is already in the URL, defer to that effect above.
    const params = new URLSearchParams(window.location.search);
    if (params.get("track")) return;

    // Pick the first audio-capable track from the library.
    const firstAudio = tracks.find((t) => {
      const types = Array.isArray(t.media_types) && t.media_types.length
        ? t.media_types
        : [t.media_type || "audio"];
      return types.includes("audio");
    }) || tracks[0];
    if (!firstAudio) return;

    // Guard against re-firing on SPA navigation within the same page load.
    autoplayAttempted.current = true;

    // Cue the first track either way so a manual Play still starts from #01.
    setMediaType("audio");
    setCurrentId(firstAudio.id);

    // Autoplay policy: music auto-starts ONLY on the first load per browser
    // "visit" (session). On subsequent reloads / navigations within the same
    // session, we cue up the track but leave it paused so the visitor decides
    // when to hit play. `sessionStorage` is the source of truth and is
    // shared with WelcomeSplash — closing the tab clears the flag naturally.
    let alreadyAutoplayed = false;
    try { alreadyAutoplayed = window.sessionStorage.getItem("syndafam-splash-autoplayed") === "1"; } catch { /* private mode */ }
    if (alreadyAutoplayed) {
      setPlaying(false);
      return undefined;
    }

    setMuted(false);
    setPlaying(true);

    // Fallback: if the browser blocked play(), the play/pause sync effect will
    // catch the promise rejection and flip `playing` back to false. Arm a
    // one-shot listener that resumes playback on the first user gesture.
    const kick = () => {
      setPlaying(true);
      window.removeEventListener("pointerdown", kick);
      window.removeEventListener("keydown", kick);
      window.removeEventListener("touchstart", kick);
      window.removeEventListener("scroll", kick);
    };
    window.addEventListener("pointerdown", kick, { once: true, passive: true });
    window.addEventListener("keydown", kick, { once: true });
    window.addEventListener("touchstart", kick, { once: true, passive: true });
    window.addEventListener("scroll", kick, { once: true, passive: true });
    return () => {
      window.removeEventListener("pointerdown", kick);
      window.removeEventListener("keydown", kick);
      window.removeEventListener("touchstart", kick);
      window.removeEventListener("scroll", kick);
    };
  }, [tracks.length]);

  const value = {
    tracks, filtered, facets, mediaType, setMediaType,
    search, setSearch, artistFilter, setArtistFilter, genreFilter, setGenreFilter,
    currentTrack, currentId, playTrack, togglePlay, nextTrack, prevTrack,
    playing, setPlaying, progress, seek, pitch, setPitch,
    shuffle, setShuffle, repeat, setRepeat,
    volume, setVolume, muted, setMuted,
    fullPlayerVisible, setFullPlayerVisible,
    videoSlot, setVideoSlot,
    audioRef, ytIframeRef, onEnded,
    showMini, setShowMini,
    refresh, playerCfg,
    MEDIA_TYPES,
    // EQ
    eqEnabled, setEqEnabled, eqBands, setEqBands, updateEqBand,
    eqPreset, setEqPreset, applyEqPreset, resetEq, EQ_PRESETS,
    eqAvailable,
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useMediaPlayer() {
  const v = useContext(Ctx);
  if (!v) throw new Error("useMediaPlayer must be used inside <MediaPlayerProvider>");
  return v;
}

// Persistent media elements mounted at the app root so playback survives route
// changes. Two elements live here:
//   1. `<audio>` for regular audio tracks.
//   2. `<iframe>` for YouTube-backed tracks — the iframe is parked ALWAYS
//      OFF-SCREEN (fixed at -9999px, 1×1, opacity 0). It streams the YouTube
//      AUDIO only, controlled via postMessage from the mediaPlayer effects.
//      The video itself is never rendered visually anywhere on the site; the
//      on-page FamRadioPlayer shows the track's YouTube thumbnail (or its
//      artwork) as a static still instead. This keeps playback alive when the
//      visitor navigates from Home to Connect / About / etc.
export function PersistentMediaElements() {
  const {
    audioRef, ytIframeRef, currentTrack, playing, setPlaying, onEnded,
    videoSlot, fullPlayerVisible,
  } = useMediaPlayer();

  // Track the video-slot bounding rect so we can overlay the persistent
  // iframe exactly on top of it (using position:fixed). We keep the iframe
  // mounted in one place in the DOM so YouTube playback state is never
  // interrupted — we only move / resize its wrapper.
  const [slotRect, setSlotRect] = useState(null);
  const wrapperRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  useEffect(() => {
    if (!videoSlot || !fullPlayerVisible) {
      setSlotRect(null);
      return undefined;
    }
    const update = () => {
      const r = videoSlot.getBoundingClientRect();
      setSlotRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    };
    update();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(update) : null;
    if (ro) ro.observe(videoSlot);
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    // Also poll briefly to catch layout shifts from font/image loads.
    const t1 = setTimeout(update, 300);
    const t2 = setTimeout(update, 1200);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [videoSlot, fullPlayerVisible, currentTrack?.id]);

  // Sync the fullscreen indicator with the browser's real fullscreen state so
  // hitting ESC (or the OS gesture) instantly restores the wrapper to slot.
  useEffect(() => {
    const onChange = () => {
      const fsEl = document.fullscreenElement || document.webkitFullscreenElement;
      setIsFullscreen(fsEl === wrapperRef.current);
    };
    document.addEventListener("fullscreenchange", onChange);
    document.addEventListener("webkitfullscreenchange", onChange);
    return () => {
      document.removeEventListener("fullscreenchange", onChange);
      document.removeEventListener("webkitfullscreenchange", onChange);
    };
  }, []);

  const toggleFullscreen = useCallback(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const fsEl = document.fullscreenElement || document.webkitFullscreenElement;
    if (fsEl === el) {
      (document.exitFullscreen || document.webkitExitFullscreen).call(document);
      return;
    }
    const req = el.requestFullscreen || el.webkitRequestFullscreen;
    if (req) {
      try { const p = req.call(el); if (p && p.catch) p.catch(() => {}); } catch { /* denied */ }
    }
  }, []);

  if (!currentTrack) return null;

  // Audio-only track — hidden <audio> element.
  if (!currentTrack.youtube_id) {
    return (
      <div className="hidden" aria-hidden="true">
        <audio
          key={`persistent-${currentTrack.id}`}
          ref={audioRef}
          src={currentTrack.audio_url}
          autoPlay={playing}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onEnded={onEnded}
          data-testid="persistent-audio"
        />
      </div>
    );
  }

  // YouTube track — the iframe lives here permanently. When a video slot is
  // registered by the on-page FamRadioPlayer AND that player is on-screen,
  // the wrapper is repositioned to overlay the slot so the visitor sees the
  // real YouTube video (with native controls). Otherwise the wrapper is
  // parked off-screen so playback continues as audio-only across pages.
  const inSlot = !!slotRect;
  // In real (native) fullscreen the browser handles sizing — we let it own
  // the box. Our inline `wrapperStyle` still applies but the browser layers
  // on top of it so the video fills the screen either way.
  const wrapperStyle = isFullscreen
    ? {
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        zIndex: 9999,
        pointerEvents: "auto",
        overflow: "hidden",
        background: "#000",
      }
    : inSlot
    ? {
        position: "fixed",
        top: slotRect.top,
        left: slotRect.left,
        width: slotRect.width,
        height: slotRect.height,
        zIndex: 5,
        pointerEvents: "auto",
        overflow: "hidden",
        borderRadius: 16,
      }
    : {
        position: "fixed",
        top: -9999,
        left: -9999,
        width: 1,
        height: 1,
        opacity: 0,
        pointerEvents: "none",
        overflow: "hidden",
      };

  const showFsBtn = inSlot || isFullscreen;

  return (
    <div
      ref={wrapperRef}
      style={wrapperStyle}
      aria-hidden={inSlot || isFullscreen ? undefined : "true"}
      data-testid="persistent-yt-container"
      data-in-slot={inSlot ? "1" : "0"}
      data-fullscreen={isFullscreen ? "1" : "0"}
    >
      <iframe
        key={`persistent-yt-${currentTrack.id}`}
        ref={ytIframeRef}
        title={currentTrack.title || "Now playing"}
        src={`https://www.youtube.com/embed/${currentTrack.youtube_id}?autoplay=1&enablejsapi=1&rel=0&modestbranding=1&playsinline=1&cc_load_policy=0&cc_lang_pref=en&hl=en`}
        allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
        allowFullScreen
        onLoad={(e) => {
          try {
            const w = e.currentTarget.contentWindow;
            w.postMessage(JSON.stringify({ event: "listening" }), "*");
            w.postMessage(JSON.stringify({ event: "command", func: "addEventListener", args: ["onStateChange"] }), "*");
            // Ensure audio is on — some ad chains re-mute the player on load.
            w.postMessage(JSON.stringify({ event: "command", func: "unMute", args: [] }), "*");
            w.postMessage(JSON.stringify({ event: "command", func: "setVolume", args: [100] }), "*");
          } catch { /* cross-origin */ }
        }}
        style={{ width: "100%", height: "100%", border: 0, display: "block" }}
        data-testid="persistent-yt-iframe"
      />
      {showFsBtn && (
        <button
          type="button"
          onClick={toggleFullscreen}
          className="absolute top-3 right-3 z-10 inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-black/60 backdrop-blur-md border border-white/20 text-white/90 hover:text-[#FCA311] hover:border-[#FCA311] transition-colors shadow-[0_2px_10px_rgba(0,0,0,0.5)]"
          title={isFullscreen ? "Exit fullscreen (Esc)" : "Expand video to fullscreen"}
          aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
          data-testid="persistent-yt-fullscreen"
        >
          {isFullscreen ? (
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 3v6H3M15 3v6h6M9 21v-6H3M15 21v-6h6"/></svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9V3h6M21 9V3h-6M3 15v6h6M21 15v6h-6"/></svg>
          )}
          <span className="font-mono text-[10px] tracking-[0.3em] hidden sm:inline">{isFullscreen ? "EXIT" : "THEATRE"}</span>
        </button>
      )}
    </div>
  );
}
